/**
 * Admin API client for beta access management
 */

import { auth } from '../lib/firebase/config';

const API_BASE = '/api/access/admin';

/**
 * Get Firebase auth token for authenticated requests
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Authentication required');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
}

export interface AllowlistEntry {
  id: string;
  email: string;
  status: 'active' | 'disabled';
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccessRequest {
  id: string;
  email: string;
  name: string;
  useCase: string;
  additionalDetails: string | null;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
  updatedAt: string;
}

export async function fetchAllowlist(): Promise<AllowlistEntry[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/allowlist`, {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch allowlist: ${response.statusText}`);
  }

  const data = await response.json();
  return data.allowlist;
}

export async function addToAllowlist(email: string, notes?: string): Promise<AllowlistEntry> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/allowlist`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ email, notes }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to add to allowlist: ${response.statusText}`);
  }

  const data = await response.json();
  return data.entry;
}

export async function removeFromAllowlist(email: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/allowlist?email=${encodeURIComponent(email)}`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to remove from allowlist: ${response.statusText}`);
  }
}

export async function fetchAccessRequests(status?: 'pending' | 'approved' | 'denied'): Promise<AccessRequest[]> {
  const url = status ? `${API_BASE}/requests?status=${status}` : `${API_BASE}/requests`;
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch requests: ${response.statusText}`);
  }

  const data = await response.json();
  return data.requests;
}

export async function updateAccessRequest(
  id: string,
  action: 'approve' | 'deny',
  notes?: string
): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/requests?id=${id}`, {
    method: 'PATCH',
    headers,
    credentials: 'include',
    body: JSON.stringify({ action, notes }),
  });

  if (!response.ok) {
    throw new Error(`Failed to ${action} request: ${response.statusText}`);
  }
}
